
  import { defineComponent, nextTick } from 'vue';

  export default defineComponent({
    name: 'ContextMenu',
    inheritAttrs: false,
    props: {
      isVisible: {
        type: Boolean,
        default: false,
      },
      position: {
        type: Object as () => { left?: number; right?: number; top: number },
        required: true,
      },
    },
    emits: ['close'],
    data() {
      return {
        menuStyle: {
          left: 'auto',
          right: 'auto',
          top: 'auto',
        },
      };
    },
    watch: {
      async isVisible(newVal: boolean) {
        if (newVal) {
          await nextTick();
          this.updateMenuStyle();
        }
      },
      position: {
        async handler() {
          if (this.isVisible) {
            await nextTick();
            this.updateMenuStyle();
          }
        },
        deep: true,
      },
    },
    mounted() {
      document.addEventListener('click', this.handleClickOutside);
      document.addEventListener('keydown', this.handleEscape);
      window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount() {
      document.removeEventListener('click', this.handleClickOutside);
      document.removeEventListener('keydown', this.handleEscape);
      window.removeEventListener('resize', this.handleResize);
    },
    methods: {
      updateMenuStyle() {
        const contextMenuRef = this.$refs.contextMenuRef as HTMLElement | undefined;
        if (!contextMenuRef) return;

        const applyPosition = () => {
          const menuWidth = contextMenuRef.offsetWidth;
          const menuHeight = contextMenuRef.offsetHeight;
          if (!menuWidth || !menuHeight) return;

          let left = this.position.left;
          let right = this.position.right;
          let top = this.position.top;

          const scrollX = window.scrollX;
          const scrollY = window.scrollY;
          const viewportLeft = scrollX;
          const viewportRight = scrollX + window.innerWidth;
          const viewportTop = scrollY;
          const viewportBottom = scrollY + window.innerHeight;

          if (typeof left === 'number') {
            if (left + menuWidth > viewportRight) {
              left -= menuWidth;
            }
            if (left < viewportLeft) {
              left = viewportLeft;
            }
          }

          if (typeof right === 'number' && right + menuWidth > window.innerWidth) {
            right -= menuWidth;
          }

          if (top + menuHeight > viewportBottom) {
            top = Math.max(viewportTop, viewportBottom - menuHeight);
          }
          if (top < viewportTop) {
            top = viewportTop;
          }

          this.menuStyle = {
            left: typeof left === 'number' ? `${left}px` : 'auto',
            right: typeof right === 'number' ? `${right}px` : 'auto',
            top: `${top}px`,
          };
        };

        if (contextMenuRef.offsetWidth && contextMenuRef.offsetHeight) {
          applyPosition();
        } else {
          requestAnimationFrame(applyPosition);
        }
      },

      handleClickOutside(event: Event) {
        const target = event.target as HTMLElement;
        if (!target.closest('.context-menu')) {
          this.$emit('close');
        }
      },

      handleEscape(event: KeyboardEvent) {
        if (event.key === 'Escape') {
          this.$emit('close');
        }
      },
      handleResize() {
        this.$emit('close');
      },
    },
  });
